import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "./supabase";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  .rp-fade { animation: fadeUp 0.45s ease both; }
`;

const inputStyle = {
  width: "100%",
  background: "#ffffff",
  border: "1px solid #e8e1d3",
  borderRadius: 10,
  padding: "12px 16px",
  color: "#1a1a1a",
  fontSize: 16, // 16px minimum — below this iOS zooms the webview on focus

  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
};

const labelStyle = {
  fontSize: 12,
  color: "#5c574e",
  fontWeight: 600,
  marginBottom: 7,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady]         = useState(false);
  const [expired, setExpired]     = useState(false);
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState(false);

  useEffect(() => {
    // Give Supabase a moment to process the URL hash token, then check session
    const timeout = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) setReady(true);
      else setExpired(true);
    }, 800);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        clearTimeout(timeout);
        setExpired(false);
        setReady(true);
      }
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async () => {
    if (!password || !confirm) { setError("Please fill in both fields."); return; }
    if (password.length < 8)   { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm)  { setError("Passwords don't match."); return; }
    setError(""); setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) { setError(updateError.message); return; }
    setSuccess(true);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f3", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column" }}>
      <style>{STYLES}</style>

      {/* Nav */}
      <div style={{ background: "#faf8f3", borderBottom: "1px solid rgba(0,0,0,0.08)", padding: "0 24px", height: 58, display: "flex", alignItems: "center", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.02em" }}>Scorify</div>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 16px 60px" }}>
        <div className="rp-fade" style={{
          background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 20, padding: "40px 36px",
          width: "100%", maxWidth: 420,
          boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
        }}>

          {/* Loading state */}
          {!ready && !expired && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <span style={{ display: "inline-block", width: 32, height: 32, border: "3px solid #8b1e2d", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              <div style={{ fontSize: 14, color: "#5c574e", marginTop: 14 }}>Verifying reset link…</div>
            </div>
          )}

          {/* Expired / invalid token */}
          {expired && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(192,57,43,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: "#1a1a1a", marginBottom: 12, letterSpacing: "-0.02em" }}>
                Link expired
              </h1>
              <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.6, marginBottom: 28 }}>
                This reset link is invalid or has expired. Request a new one.
              </p>
              <button onClick={() => navigate("/login")} style={{ background: "#8b1e2d", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
                Back to sign in
              </button>
            </div>
          )}

          {/* Success state */}
          {ready && success && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(45,106,79,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#2d6a4f" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: "#1a1a1a", marginBottom: 12, letterSpacing: "-0.02em" }}>
                Password updated!
              </h1>
              <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.6, marginBottom: 28 }}>
                You can now sign in with your new password.
              </p>
              <button onClick={() => navigate("/login")} style={{ background: "#8b1e2d", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
                Go to sign in
              </button>
            </div>
          )}

          {/* Reset form */}
          {ready && !success && (
            <>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, fontWeight: 400, letterSpacing: "-0.02em", color: "#1a1a1a", marginBottom: 8 }}>
                  Set a new password
                </h1>
                <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.5 }}>Choose a strong password for your account.</p>
              </div>

              {error && (
                <div style={{ background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#c0392b", fontWeight: 500, marginBottom: 20 }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelStyle}>New password</label>
                  <input type="password" placeholder="Min. 8 characters" value={password}
                    onChange={e => { setPassword(e.target.value); setError(""); }}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Confirm password</label>
                  <input type="password" placeholder="••••••••" value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    style={{ ...inputStyle, borderColor: confirm && confirm !== password ? "rgba(192,57,43,0.4)" : undefined }} />
                  {confirm && confirm !== password && (
                    <div style={{ fontSize: 11, color: "#c0392b", marginTop: 5, fontWeight: 500 }}>Passwords don't match</div>
                  )}
                </div>

                <button onClick={handleSubmit} disabled={loading} style={{
                  width: "100%", background: "#8b1e2d", color: "#fff", border: "none",
                  borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif", cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1, marginTop: 6,
                  boxShadow: "0 4px 20px rgba(139,30,45,0.2)", transition: "opacity 0.15s",
                }}>
                  {loading
                    ? <span style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    : "Update password →"}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
